import { isDefined } from "@clipboard-health/util-ts";

const SAFE_API_SERVICE_PATH_SEGMENTS = new Set([
  "home-health-api",
  "license-manager",
  "payment",
  "worker",
]);
const MAX_RAW_PATH_SEGMENT_LENGTH = 128;

interface SanitizeApiPathTemplateParams {
  pathname: string;
  maxPathSegments: number;
}

export function sanitizeApiPathTemplate(params: SanitizeApiPathTemplateParams): string {
  const { maxPathSegments, pathname } = params;
  const pathSegments = pathname
    .split("/", maxPathSegments + 2)
    .filter((segment) => segment.length > 0);
  const pathWasTruncated = pathSegments.length > maxPathSegments;
  const decodedSegments = pathSegments
    .slice(0, maxPathSegments)
    .map((segment) => decodePathSegment({ segment }));
  const sanitizedSegments = decodedSegments.map(() => ":id");

  preserveApiPathPrefix({ decodedSegments, sanitizedSegments });
  preserveHomeHealthCasesPath({ decodedSegments, sanitizedSegments });

  if (pathWasTruncated) {
    sanitizedSegments.push(":truncated");
  }

  return `/${sanitizedSegments.join("/")}`;
}

function decodePathSegment(params: { segment: string }): string | undefined {
  const { segment } = params;

  if (segment.length > MAX_RAW_PATH_SEGMENT_LENGTH) {
    return undefined;
  }

  try {
    const decodedSegment = decodeURIComponent(segment);

    return decodedSegment.includes("/") ? undefined : decodedSegment;
  } catch {
    return undefined;
  }
}

function preserveApiPathPrefix(params: {
  decodedSegments: (string | undefined)[];
  sanitizedSegments: string[];
}): void {
  const { decodedSegments, sanitizedSegments } = params;
  const firstSegment = decodedSegments[0];

  if (firstSegment === "api") {
    sanitizedSegments[0] = "api";
    preserveVersionSegment({ decodedSegments, sanitizedSegments, versionIndex: 1 });
    return;
  }

  if (!isDefined(firstSegment) || !SAFE_API_SERVICE_PATH_SEGMENTS.has(firstSegment)) {
    return;
  }

  sanitizedSegments[0] = firstSegment;
  if (decodedSegments[1] === "api") {
    sanitizedSegments[1] = "api";
    preserveVersionSegment({ decodedSegments, sanitizedSegments, versionIndex: 2 });
  }
}

function preserveVersionSegment(params: {
  decodedSegments: (string | undefined)[];
  sanitizedSegments: string[];
  versionIndex: number;
}): void {
  const { decodedSegments, sanitizedSegments, versionIndex } = params;
  const versionSegment = decodedSegments[versionIndex];

  if (isDefined(versionSegment) && /^v\d+$/.test(versionSegment)) {
    sanitizedSegments[versionIndex] = versionSegment;
  }
}

function preserveHomeHealthCasesPath(params: {
  decodedSegments: (string | undefined)[];
  sanitizedSegments: string[];
}): void {
  const { decodedSegments, sanitizedSegments } = params;

  if (
    decodedSegments[0] === "home-health-api" &&
    decodedSegments[1] === "api" &&
    /^v\d+$/.test(decodedSegments[2] ?? "") &&
    decodedSegments[4] === "cases"
  ) {
    sanitizedSegments[4] = "cases";
  }
}
